import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export function LegalSection({ title, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, ease: EASE }}
    >
      <h2 className="mb-3 font-display text-xl font-semibold tracking-tight text-navy">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-slate-600 sm:text-base">{children}</div>
    </motion.section>
  );
}

export default function LegalLayout({ title, intro, children, testId }) {
  return (
    <div className="min-h-screen bg-slate-50" data-testid={testId}>
      <header className="noise-overlay bg-navy">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-6">
          <Link to="/" data-testid={`${testId}-logo`} className="group flex items-center">
            <img
              src="/images/revanta-logo.png"
              alt="Revanta Asset Management"
              className="h-14 w-auto transition-transform duration-300 group-hover:scale-[1.03]"
            />
          </Link>
          <Link
            to="/"
            data-testid={`${testId}-back-link`}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-eco"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
        </div>
        <div className="mx-auto max-w-3xl px-6 pb-16 pt-10">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl"
            data-testid={`${testId}-title`}
          >
            {title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-4 text-sm text-slate-400"
            data-testid={`${testId}-intro`}
          >
            {intro}
          </motion.p>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <div className="flex flex-col gap-10">{children}</div>
      </main>

      <footer className="border-t border-slate-200 py-8">
        <div className="mx-auto flex max-w-3xl flex-col items-start justify-between gap-3 px-6 sm:flex-row sm:items-center">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} Revanta Asset Management — Australia
          </p>
          <div className="flex gap-5">
            <Link to="/privacy" data-testid={`${testId}-footer-privacy`} className="text-xs font-medium text-slate-500 transition-colors hover:text-eco-dark">
              Privacy Policy
            </Link>
            <Link to="/terms" data-testid={`${testId}-footer-terms`} className="text-xs font-medium text-slate-500 transition-colors hover:text-eco-dark">
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
